import React from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, User, ArrowRight, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { POSTS, getPostBySlug } from "@/constants/blog";

export default function BlogPostPage({ slug }) {
  const post = getPostBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`${post.title} | Vila Elevators Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        keywords={post.keywords}
        image={post.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          image: post.image,
          datePublished: post.date,
          dateModified: post.date,
          author: { "@type": "Organization", name: post.author },
          publisher: {
            "@type": "Organization",
            name: "Vila Elevators & Escalators",
            logo: {
              "@type": "ImageObject",
              url: "https://vilaelevators.in/brand/vila-logo-dark.png",
            },
          },
          mainEntityOfPage: `https://vilaelevators.in/blog/${post.slug}`,
          description: post.excerpt,
        }}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 border-b border-[#e5ddd0] overflow-hidden">
        <img src={post.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#faf7f2]/95 via-[#faf7f2]/90 to-[#faf7f2]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 pt-8">
          <div className="flex items-center gap-2 text-xs text-[#8a8a8a] mb-8">
            <Link to="/" className="hover:text-[#b08d57]">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-[#b08d57]">Blog</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#4a4a4a] truncate max-w-xs">{post.category}</span>
          </div>
          <div className="inline-block bg-[#b08d57] text-white text-[10px] uppercase font-bold tracking-widest px-3 py-1 mb-6">
            {post.category}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#1a1a1a] leading-[1.05] mb-8"
          >
            {post.title}
          </motion.h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-[#6b6b6b] border-t border-[#e5ddd0] pt-6">
            <span className="flex items-center gap-2"><User className="w-4 h-4 text-[#b08d57]" /> {post.author}</span>
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#b08d57]" /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#b08d57]" /> {post.readTime}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="border border-[#e5ddd0] aspect-[16/9] overflow-hidden mb-12">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>

          <div className="prose-vila space-y-6">
            <p className="text-xl text-[#4a4a4a] leading-relaxed border-l-2 border-[#b08d57] pl-6 italic">
              {post.excerpt}
            </p>
            {post.body.map((block, i) => {
              if (block.h2) {
                return (
                  <h2 key={i} className="font-display text-2xl sm:text-3xl font-bold text-[#1a1a1a] mt-12 mb-4">
                    {block.h2}
                  </h2>
                );
              }
              if (block.p) {
                return (
                  <p key={i} className="text-[#4a4a4a] text-lg leading-relaxed">
                    {block.p}
                  </p>
                );
              }
              if (block.img) {
                return (
                  <figure key={i} className="my-8">
                    <img
                      src={block.img}
                      alt={block.alt || post.title}
                      loading="lazy"
                      className="w-full border border-[#e5ddd0]"
                      data-testid={`post-inline-image-${i}`}
                    />
                    {block.caption && (
                      <figcaption className="text-sm text-[#8a8a8a] mt-3 text-center">{block.caption}</figcaption>
                    )}
                  </figure>
                );
              }
              return null;
            })}
          </div>

          {/* CTA */}
          <div className="mt-16 border border-[#b08d57]/40 bg-[#b08d57]/5 p-8 lg:p-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-3">Ready to talk?</div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-3">Get a personalized quote for your building.</h3>
            <p className="text-[#4a4a4a] mb-6">Our engineering team will review your requirements and respond within one business day.</p>
            <Link to="/contact" data-testid={`blog-post-cta-${post.slug}`} className="inline-flex items-center gap-2 bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors">
              Request A Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>

      {/* Post FAQs — AEO */}
      {post.faqs && <FAQSection faqs={post.faqs} title="Quick answers" />}

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 bg-[#faf7f2]/40 border-t border-[#e5ddd0]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Continue Reading</div>
            <h3 className="font-display text-3xl font-bold text-[#1a1a1a] mb-10">Related articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`related-blog-${r.slug}`}
                  className="group border border-[#e5ddd0] hover:border-[#b08d57]/50 bg-[#faf7f2] overflow-hidden transition-colors"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="text-[10px] uppercase tracking-widest text-[#b08d57] mb-2">{r.category}</div>
                    <div className="font-display text-lg font-bold text-[#1a1a1a] mb-2 line-clamp-2">{r.title}</div>
                    <div className="text-[#6b6b6b] text-sm line-clamp-2">{r.excerpt}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
